import React from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { URL } from "constants/api/requests"

import { AdminPlayerBonus2 } from "services/bonus/type"

import { formatDateTime, formatFiatCurrency } from "util/formatter"

import { getDisplayName, isExpired,isExpiringSoon } from "../../helpers/bonus2.helpers"
import { formatStake } from "../../helpers/spin-stake.helpers"

interface FreeSpinsTableProps {
  freeSpins: AdminPlayerBonus2[]
  onDetails: (pb: AdminPlayerBonus2) => void
  onAward: (pb: AdminPlayerBonus2) => void
  onExtend: (pb: AdminPlayerBonus2) => void
  onCancel: (pb: AdminPlayerBonus2) => void
}

const TABLE_HEADERS = [
  { label: "bonus2.spins.thPlayer", align: "left" },
  { label: "bonus2.spins.thBonus", align: "left" },
  { label: "bonus2.spins.thSpins", align: "left" },
  { label: "bonus2.spins.thStake", align: "left" },
  { label: "bonus2.spins.thWinnings", align: "left" },
  { label: "bonus2.spins.thExpires", align: "left" },
  { label: "bonus2.spins.thActions", align: "right" }
] as const

export const FreeSpinsTable: React.FC<FreeSpinsTableProps> = ({ freeSpins, onDetails, onAward, onExtend, onCancel }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const handleExtendClick = (pb: AdminPlayerBonus2) => {
    if (pb.free_spins_remaining === 0) {
      toast.warning(t("bonus2.spins.toastNoneToExtend") as string)
      return
    }
    onExtend(pb)
  }

  const handleCancelClick = (pb: AdminPlayerBonus2) => {
    if (pb.free_spins_remaining === 0) {
      toast.warning(t("bonus2.spins.toastNoneToCancel") as string)
      return
    }
    onCancel(pb)
  }

  if (freeSpins.length === 0) {
    return <div className="text-center text-gray-400 py-12 bg-[#1A1A2E] rounded-lg">{t("bonus2.spins.noActiveFreeSpins")}</div>
  }

  return (
    <div className="bg-[#1A1A2E] rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-[#0C0C1A]">
          <tr>
            {TABLE_HEADERS.map((header) => (
              <th key={header.label} className={`px-4 py-3 text-${header.align} text-sm font-medium text-gray-300`}>
                {t(header.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {freeSpins.map((pb) => (
            <tr key={pb.id} className={isExpired(pb.free_spins_expires_at) ? "opacity-50" : ""}>
              <td className="px-4 py-3">
                {/* Readable player identifier linking straight to the player profile; the raw UUID
                    is only used as a fallback when core-api could not join the username. */}
                <a
                  href={URL.USER.replace(":id", pb.user_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="link link-primary text-sm"
                  title={pb.user_id}
                >
                  {pb.username || pb.user_id}
                </a>
              </td>
              <td className="px-4 py-3">
                <div className="text-gray-300 text-sm">{getDisplayName(pb.bonus?.name, t("bonus2.shared.unknownBonus") as string)}</div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-400 font-bold">{pb.free_spins_remaining}</span>
                  <span className="text-gray-500 text-xs">/ {pb.free_spins_awarded}</span>
                </div>
                <div className="text-gray-500 text-xs">{pb.free_spins_used} used</div>
              </td>
              {/* SOFT-736 — the stake FROZEN when these spins were awarded, in the player's own
                  coin (the template's field is USD, and editing the template no longer changes an
                  existing grant). `—` for spins granted before the freeze existed: those still
                  follow the template, which is not the same thing as a stake of zero. */}
              <td className="px-4 py-3">
                <div className="text-gray-300 text-sm">
                  {pb.free_spins_bet_value === null || pb.free_spins_bet_value === undefined ? (
                    <span className="text-gray-500" title={t("bonus2.spins.awardedBeforeTheStake")}>
                      —
                    </span>
                  ) : (
                    formatStake(pb.free_spins_bet_value, pb.coin)
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="text-yellow-400 font-semibold">{formatFiatCurrency(Number(pb.free_spins_winnings))}</div>
              </td>
              <td className="px-4 py-3">
                {pb.free_spins_expires_at ? (
                  <div className="flex flex-col">
                    <span
                      className={`text-sm ${
                        isExpired(pb.free_spins_expires_at)
                          ? "text-rose-400"
                          : isExpiringSoon(pb.free_spins_expires_at)
                          ? "text-orange-400"
                          : "text-gray-300"
                      }`}
                    >
                      {formatDateTime(pb.free_spins_expires_at)}
                    </span>
                    {isExpired(pb.free_spins_expires_at) && <span className="text-rose-400 text-xs font-semibold">{t("bonus2.spins.expired")}</span>}
                    {isExpiringSoon(pb.free_spins_expires_at) && !isExpired(pb.free_spins_expires_at) && (
                      <span className="text-orange-400 text-xs font-semibold">⚠️ Expiring soon</span>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">{t("bonus2.spins.noExpiry")}</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => onDetails(pb)}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded transition-all hover:bg-blue-700"
                  >
                    {t("bonus2.spins.details")}
                  </button>
                  <button
                    onClick={() => onAward(pb)}
                    className="px-3 py-1 text-xs bg-purple text-white rounded transition-all hover:bg-purple"
                  >
                    + Award
                  </button>
                  <button
                    onClick={() => handleExtendClick(pb)}
                    className={`px-3 py-1 text-xs text-white rounded transition-all ${
                      pb.free_spins_remaining === 0 ? "bg-emerald-600/50" : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                  >
                    {t("bonus2.spins.extend")}
                  </button>
                  <button
                    onClick={() => handleCancelClick(pb)}
                    className={`px-3 py-1 text-xs text-white rounded transition-all ${
                      pb.free_spins_remaining === 0 ? "bg-rose-500/50" : "bg-rose-500 hover:bg-rose-700"
                    }`}
                  >
                    {t("bonus2.spins.cancel")}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
